import { StateCreator } from 'zustand';
import { addMonths, format } from 'date-fns';
import * as appointmentService from '@/services/appointment.service';
import { addMinutes } from '@/utils/utils';
import { setsterServices, setsterServicesDateNight } from '@/consts/appointment';
import { IAppointment } from '../types/IAppointment';
import INITIAL_STATE from '../initialState';

/**
 * This file defines the appointmentSlice, a state management slice for handling appointment-related actions and state.
 * It exports a function `createAppointmentSlice` that returns an object with methods for managing appointment booking information.
 *
 * The slice includes methods for:
 * - `getSetsterAvailabilityDateTimes`: Fetches availability date times for a given service and location over a period of 13 months.
 * - `getSetsterProviderId`: Retrieves the provider ID for a given appointment booking.
 * - `bookAppointment`: Initiates the booking process for an appointment.
 * - `setAppointmentBookingInformation`: Updates the appointment booking information state.
 *
 * The slice state includes properties for:
 * - `appointmentBookingInformation`: Holds the current state of appointment booking information.
 *
 * This slice is designed to manage the state and actions related to appointment booking, including fetching availability, booking appointments, and updating booking information.
 */

const createAppointmentSlice: StateCreator<IAppointment> = (set, get) => ({
  appointmentBookingInformation: INITIAL_STATE.appointment,
  getSetsterAvailabilityDateTimes: async (serviceId, locationId, startDate, timezone = '') => {
    const { companyId } = get().appointmentBookingInformation;
    const setsterData = [];
    let yearlySetsterAvailabilityDateTimes = {};

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 13; i++) {
      setsterData.push(
        appointmentService.getSetsterAvailabilityDateTimes(
          serviceId,
          locationId,
          format(addMonths(new Date(startDate), i), 'yyyy-MM-dd'),
          companyId,
          timezone
        ) as unknown as never
      );
    }
    let newDateTimes = {};
    try {
      // eslint-disable-next-line no-plusplus, no-restricted-syntax
      for (const promise of setsterData) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await promise;
        yearlySetsterAvailabilityDateTimes = {
          ...yearlySetsterAvailabilityDateTimes,
          times: {
            ...(yearlySetsterAvailabilityDateTimes as any)?.times,
            ...(data as any).data?.times,
          },
        };

        const weeks = (yearlySetsterAvailabilityDateTimes as any).times;
        const days = Object.keys(weeks);
        const formattedTimes = {};
        const { interval } = yearlySetsterAvailabilityDateTimes as any;

        // Fixed the interval to 50 minutes for "Engagement Ring"  "56861", "Wedding Ring" "56862", "Fine Jewelry & Gifts" "56863" and 15 minutes for Drop off and Pick up services and 75 minutes for Date Night.
        let formattedInterval = 0;
        if (
          serviceId === setsterServices.engagementRing ||
          serviceId === setsterServices.weddingRing ||
          serviceId === setsterServices.fineJewelryGifts
        ) {
          formattedInterval = 50;
        } else if (serviceId === setsterServices.dropOff || serviceId === setsterServices.pickUp) {
          formattedInterval = 15;
        } else if (Object.values(setsterServicesDateNight).includes(serviceId)) {
          formattedInterval = 75;
        } else {
          formattedInterval = interval;
        }

        days.forEach(day => {
          formattedTimes[day] = weeks[day].map(time => {
            // eslint-disable-next-line no-nested-ternary
            const startFormat = time[0] === '0' && time[1] !== '0' ? time.slice(1, 5) : time.slice(0, 5);

            const endTime = addMinutes(time, formattedInterval);

            // eslint-disable-next-line no-nested-ternary
            const endTimeFormat = endTime[0] === '0' && endTime[1] !== '0' ? endTime.slice(1, 5) : endTime;
            return {
              id: `day-${day}-${startFormat}`,
              start: startFormat,
              end: endTimeFormat,
            };
          });
        });

        newDateTimes = {
          ...formattedTimes,
        };
      }
      if (Object.values(setsterServicesDateNight).includes(serviceId)) {
        set(state => ({
          appointmentBookingInformation: {
            ...state.appointmentBookingInformation,
            setsterAvailabilityDateTimesLoading: false,
            setsterAvailabilityDateNightTimes: newDateTimes,
          },
        }));
      } else {
        set(state => ({
          appointmentBookingInformation: {
            ...state.appointmentBookingInformation,
            setsterAvailabilityDateTimesLoading: false,
            setsterAvailabilityDateTimes: newDateTimes,
          },
        }));
      }
      set(state => ({
        appointmentBookingInformation: {
          ...state.appointmentBookingInformation,
          isSetsterError: false,
        },
      }));
      return newDateTimes;
    } catch (error) {
      set(state => ({
        appointmentBookingInformation: {
          ...state.appointmentBookingInformation,
          isSetsterError: true,
        },
      }));
      throw new Error('Error in getSetsterAvailabilityDateTimes: ', {
        cause: 'getSetsterAvailabilityDateTimes',
      });
    }
  },
  getSetsterProviderId: async (isDateNight: boolean) => {
    try {
      const {
        companyId,
        selectedShowroomStore: { id: locationId },
        selectedServiceId: StandardAppointmentServiceId,
        selectedServiceDateNightId: DateNightAppointmentServiceId,
        selectedTimezoneId: timezoneId,
        selectedDate,
        selectedTime,
      } = get().appointmentBookingInformation;
      const date = `${selectedDate?.getFullYear()}-${selectedDate!.getMonth() + 1 < 10 ? `0${selectedDate!.getMonth() + 1}` : `${selectedDate!.getMonth() + 1}`}-${selectedDate?.getDate()}`;
      let time: string;
      const timeSplit = selectedTime.split(':');
      if (selectedTime.includes('PM') && `${timeSplit[0][0]}${timeSplit[0][1]}` !== '12') {
        time = `${parseInt(timeSplit[0], 10) + 12}:${timeSplit[1].slice(0, 2)}`;
      } else {
        time = `${timeSplit[0]}:${timeSplit[1].slice(0, 2)}`;
      }

      const datetime = `${date} ${time}`;
      const serviceId = isDateNight ? DateNightAppointmentServiceId : StandardAppointmentServiceId;

      const {
        data: { data },
      } = await appointmentService.getSetsterProviderId({
        companyId,
        locationId,
        serviceId,
        datetime,
        timezoneId,
      });

      set(state => ({
        appointmentBookingInformation: {
          ...state.appointmentBookingInformation,
          setsterProviderId: data.provider_id,
        },
      }));
    } catch (e) {
      /* empty */
    }
  },
  bookAppointment(data) {
    return appointmentService.bookAppointment(data);
  },
  setAppointmentBookingInformation(key, value) {
    set(state => ({ appointmentBookingInformation: { ...state.appointmentBookingInformation, [key]: value } }));
  },
});

export default createAppointmentSlice;
